import * as React from 'react';
import { IoTimeOutline, IoArrowForwardOutline } from 'react-icons/io5';
import { useCurrentUser } from 'hooks/useCurrentUser';
import { useMarketClockMs } from 'hooks/useMarketSessionClock';
import { useMarketCalendar } from 'context/MarketCalendarContext';
import resolveUserTimezone from 'utils/shared/time/resolveUserTimezone';
import {
  flowAnalysisTimestamp,
  formatFlowAnalysisTime,
  formatFlowAnalysisWindow,
  getFlowAnalysisSchedule,
} from 'layouts/FlowPageContainer/flowAiAnalysisSchedule';

interface Props {
  lastUpdated: string | null;
  isLoading: boolean;
}

/** Compact header metadata. Publication estimates never imply live analysis. */
export default function FlowAiAnalysisUpdateStatus({ lastUpdated, isLoading }: Props) {
  const { user } = useCurrentUser();
  const nowMs = useMarketClockMs();
  useMarketCalendar();
  const zone = resolveUserTimezone(user?.timezoneLocation);
  const schedule = nowMs === null ? null : getFlowAnalysisSchedule(nowMs, lastUpdated);
  const updatedMs = nowMs === null ? null : flowAnalysisTimestamp(lastUpdated, nowMs);
  const full = (ms: number) => formatFlowAnalysisTime(ms, zone, nowMs!);
  const compact = (ms: number) => {
    const day = new Intl.DateTimeFormat('en-CA', { timeZone: zone });
    const sameDay = day.format(ms) === day.format(nowMs!);
    return new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      ...(sameDay
        ? {}
        : {
            month: 'numeric',
            day: 'numeric',
            ...(new Intl.DateTimeFormat('en-US', { timeZone: zone, year: 'numeric' }).format(ms) ===
            new Intl.DateTimeFormat('en-US', { timeZone: zone, year: 'numeric' }).format(nowMs!)
              ? {}
              : { year: 'numeric' }),
          }),
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(ms);
  };
  const zoneName = (ms: number) =>
    new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'short' })
      .formatToParts(ms)
      .find((part) => part.type === 'timeZoneName')?.value;
  const zoneLabel = nowMs === null ? '' : zoneName(schedule?.nextUpdateMs ?? nowMs);
  const updatedZone = updatedMs === null ? '' : zoneName(updatedMs);
  const description = `Updated ${
    updatedMs === null ? (isLoading ? 'Loading' : 'Time missing') : full(updatedMs)
  }. Next read ${schedule ? full(schedule.nextUpdateMs) : 'Checking schedule'}. ${
    schedule
      ? `Every hour at :30 on US trading days, ${formatFlowAnalysisWindow(
          schedule.scheduleDate,
          zone,
        )}.`
      : 'Every hour at :30 on US trading days.'
  }`;
  return (
    <div
      className="flex min-w-0 items-center text-[10px] leading-4 text-white/60 sm:text-[11px]"
      aria-label="Analysis update times"
    >
      <div className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-1 sm:flex-col sm:items-end md:flex-row md:gap-4">
        <span className="inline-flex items-center gap-1">
          <IoTimeOutline
            aria-hidden="true"
            className="hidden h-3.5 w-3.5 shrink-0 text-gray-500 md:block"
          />
          <span aria-hidden="true">Updated</span>
          {updatedMs !== null ? (
            <time
              className="font-medium text-gray-200"
              aria-label={`Updated ${full(updatedMs)}`}
              dateTime={new Date(updatedMs).toISOString()}
            >
              {compact(updatedMs)}
            </time>
          ) : isLoading ? (
            'Loading…'
          ) : (
            'Time missing'
          )}
          {updatedZone && updatedZone !== zoneLabel && <span>{updatedZone}</span>}
        </span>
        <span className="inline-flex items-center gap-1">
          <IoArrowForwardOutline
            aria-hidden="true"
            className="hidden h-3.5 w-3.5 shrink-0 text-gray-500 md:block"
          />
          <span aria-hidden="true">Next</span>
          <span aria-hidden="true" className="text-gray-200">
            ~
          </span>
          {schedule ? (
            <time
              className="font-medium text-gray-200"
              aria-label={`Next read ${full(schedule.nextUpdateMs)}`}
              dateTime={new Date(schedule.nextUpdateMs).toISOString()}
            >
              {compact(schedule.nextUpdateMs)}
            </time>
          ) : (
            'Checking…'
          )}
          <span>{zoneLabel}</span>
        </span>
      </div>
      <span className="sr-only">{description}</span>
    </div>
  );
}
